/**
 * Scroll-reveal for the virtualized model list: a row animates in the
 * first time its key scrolls into view, and never again — the react-bits
 * animated-list feel with no autoplay and no scroll loop. Works against
 * LegendList's recycled DOM nodes by keying reveal memory on
 * `data-reveal-key`, not on the element.
 *
 * The hidden initial state only applies once the scroll element wears
 * `data-mp-reveal-armed` (set here, after the observer exists), so rows
 * stay visible wherever IntersectionObserver is unavailable.
 *
 * @module modelListReveal
 */
import { useCallback, useEffect, useRef } from "react";

const REVEAL_STAGGER_MS = 30;
const REVEAL_STAGGER_CAP = 7;

/** Per-batch cascade delay: 30ms steps, capped so long lists stay snappy. */
export function revealCascadeDelayMs(batchIndex: number): number {
  return Math.min(batchIndex, REVEAL_STAGGER_CAP) * REVEAL_STAGGER_MS;
}

export function useModelListReveal(input: {
  /** Stable getter for the list's scrollable element (null before mount). */
  getScrollElement: () => HTMLElement | null;
  /**
   * Reveal-memory context (instance / search mode). Changing it clears the
   * revealed set so the new list cascades fresh; scrolling back within one
   * context never re-animates.
   */
  contextKey: string;
  /** Identity of the rendered item keys; re-syncs observation after list changes. */
  itemsKey: unknown;
}): { syncReveal: () => void } {
  const { getScrollElement, contextKey, itemsKey } = input;
  const revealedRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedRootRef = useRef<HTMLElement | null>(null);
  const contextRef = useRef(contextKey);

  const syncReveal = useCallback(() => {
    const scrollElement = getScrollElement();
    if (!scrollElement || typeof IntersectionObserver === "undefined") {
      return;
    }
    scrollElement.setAttribute("data-mp-reveal-armed", "");
    if (observerRef.current === null || observedRootRef.current !== scrollElement) {
      observerRef.current?.disconnect();
      observedRootRef.current = scrollElement;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          let batchIndex = 0;
          for (const entry of entries) {
            if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) {
              continue;
            }
            const row = entry.target;
            const key = row.getAttribute("data-reveal-key");
            if (key === null) {
              continue;
            }
            if (revealedRef.current.has(key)) {
              row.style.transitionDelay = "0ms";
            } else {
              revealedRef.current.add(key);
              row.style.transitionDelay = `${revealCascadeDelayMs(batchIndex)}ms`;
              batchIndex += 1;
            }
            row.classList.add("mp-reveal-in");
            observerRef.current?.unobserve(row);
          }
        },
        { root: scrollElement, threshold: 0.05 },
      );
    }
    const observer = observerRef.current;
    for (const row of scrollElement.querySelectorAll("[data-reveal-key]")) {
      if (!(row instanceof HTMLElement)) {
        continue;
      }
      const key = row.getAttribute("data-reveal-key");
      if (key === null) {
        continue;
      }
      if (revealedRef.current.has(key)) {
        row.style.transitionDelay = "0ms";
        row.classList.add("mp-reveal-in");
      } else {
        // A recycled node can wear its previous key's revealed state; strip
        // it so the new key hides until this row intersects for real.
        row.classList.remove("mp-reveal-in");
        observer.observe(row);
      }
    }
  }, [getScrollElement]);

  useEffect(() => {
    if (contextRef.current !== contextKey) {
      contextRef.current = contextKey;
      revealedRef.current.clear();
    }
    // Double rAF so LegendList lays rows out before the observer measures.
    let nestedFrame = 0;
    const frame = window.requestAnimationFrame(() => {
      syncReveal();
      nestedFrame = window.requestAnimationFrame(syncReveal);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(nestedFrame);
    };
  }, [contextKey, itemsKey, syncReveal]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return { syncReveal };
}
